import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import KBDropdown from './KBDropdown';
import {
    useContext, useEffect, useRef, useState
} from 'react';
import {
    VECTOR_DATABASE_OPTIONS, VECTOR_DIMENSIONS_OPTIONS
} from '../config/dropdownOptions';
import {
    Button
} from 'shared/components/ui/button';
import {
    Switch
} from 'shared/components/ui/switch';
import InputWithTooltip from './InputWithTooltip';
import {
    KBContext
} from '../knowledgeBuilderContext/KBGlobalContext';
import {
    useParams
} from 'react-router-dom';
import {
    redisCollectionService
} from 'shared/api';
import {
    SidebarContext
} from '../knowledgeBuilderContext/KBSidebarContext';
import {
    DialogTrigger
} from '@radix-ui/react-dialog';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    Context
} from 'shared/context/GlobalState';

export default function CollectionEditModal({
    setter = () => { }
}) {
    const {
        collectionDetails
    } = useContext(KBContext);
    const {
        vectorIndex
    } = useContext(SubOrgContext);
    const {
        addNewNotifcation
    } = useContext(Context);
    const {
        actions,
        hasMilvusCollection
    } = useContext(SubOrgContext);
    const {
        setEditDialogVisible
    } = useContext(SidebarContext);
    const [collectionName, setCollectionName] = useState(collectionDetails?.name || '');
    const [vectorStoreType, setVectorStoreType] = useState(VECTOR_DATABASE_OPTIONS.find(option => option.value === collectionDetails?.vectorStoreType) || null);
    const [dimension, setDimension] = useState(VECTOR_DIMENSIONS_OPTIONS.find(option => option.value === collectionDetails?.dimensions) || null);
    const [pdfPageImages, setPdfPageImages] = useState(collectionDetails?.pdfPageImages || false);
    const [btnDiasable, setBtnDisable] = useState(false);
    const [loading, setLoading] = useState(false);

    const {
        oragID, subOragID, collectionId
    } = useParams();

    const [confirmationVisibility, setConfirmationVisibility] = useState(false);
    const confirmation = useRef(true);
    const initialRender = useRef(true);
    const originalValues = useRef({});

    useEffect(() => {
        if (!collectionName || vectorStoreType === null || dimension === null) {
            setBtnDisable(true);
        } else {
            setBtnDisable(false);
        }
    }, [collectionName, vectorStoreType, dimension]);

    useEffect(() => {
        if (vectorStoreType?.value === 'MongoDB' && dimension?.value !== 1024) {
            setDimension(VECTOR_DIMENSIONS_OPTIONS.find(opt => opt.value === 1024) || null);
        }
    }, [vectorStoreType]);

    useEffect(() => {
        if (initialRender.current) {
            initialRender.current = false;
            originalValues.current = {
                organizationId: oragID,
                subOrganizationId: subOragID,
                collectionId: collectionId,
                dimensions: dimension?.value,
                name: collectionName,
                vectorStoreType: vectorStoreType?.value,
            };

        } else {
            confirmation.current = false;
        }

    }, [vectorStoreType, dimension]);

    const areValuesUnchanged = () => {
        return (
            dimension?.value === originalValues.current.dimensions &&
            collectionName === originalValues.current.name &&
            vectorStoreType?.value === originalValues.current.vectorStoreType
        );
    };

    const requestToUpdate = async () => {
        const updatedCollectionObj = {
            organizationId: oragID,
            subOrganizationId: subOragID,
            collectionId: collectionId,
            dimensions: dimension.value,
            name: collectionName,
            chunkingStrategy: 'by page',
            vectorStoreType: vectorStoreType.value,
            embeddingModel: 'jina',
            pdfPageImages: pdfPageImages,
        };
        return await redisCollectionService.updateCollection(updatedCollectionObj)
            .then((res) => {
                actions({
                    type: 'SET_COLLECTIONS',
                    payload: (collections => {
                        const updatedCollection = collections.map(item => {
                            if (item._id === collectionId) {
                                return {
                                    ...item,
                                    ...res.data
                                };
                            } else {
                                return item;
                            }
                        });

                        return updatedCollection;
                    })
                });

                addNewNotifcation('Sub-Organization updated successfully', 'success');
                setEditDialogVisible(false);
            })
            .catch((error) => {

                if (error?.response?.status === 404) {
                    addNewNotifcation('Collection not found', 'danger');

                } else {
                    addNewNotifcation('Something went wrong', 'danger');
                }
            })
            .finally(() => {
                setLoading(false);
                setBtnDisable(false);
                setter(false);
            });

    };

    const handleUpdateCollection = () => {

        setBtnDisable(true);
        if (confirmation.current || areValuesUnchanged()) {
            setLoading(true);
            requestToUpdate();
        }
    };

    return (
        <>
            <DialogHeader>
                <DialogTitle>
                    Edit Collection
                </DialogTitle>
                <DialogDescription>
                    Edit the collection details and settings here.
                </DialogDescription>
            </DialogHeader>
            <div>
                <div className="mb-2">
                    <InputWithTooltip placeholder="Type a collection name" required value={collectionName} setter={setCollectionName} label="Collection Name" />
                </div>
                <div className="mb-2">
                    <KBDropdown setter={setVectorStoreType} title="Vector Database" options={() => {
                        let vdOptions = VECTOR_DATABASE_OPTIONS;
                        if (!vectorIndex) {
                            vdOptions = vdOptions.filter((option) => option.value !== 'MongoDB');
                        }
                        if (!hasMilvusCollection) {
                            vdOptions = vdOptions.filter((option) => option.value !== 'Milvus');
                        }
                        return vdOptions;
                    }} required selectedValue={vectorStoreType} />
                </div>
                <div className="mb-2">
                    {vectorStoreType?.value === 'Milvus' ? (
                        <KBDropdown setter={setDimension} title="Dimension" options={() => VECTOR_DIMENSIONS_OPTIONS} required selectedValue={dimension} />
                    ) : (
                        <>
                            <label className="block text-[0.85rem] font-semibold mb-0.5">Dimension</label>
                            <span className="block text-sm text-muted-foreground">1024</span>
                        </>
                    )}
                </div>
                <div className="mb-2">
                    <label className="block text-[0.85rem] font-semibold mb-0.5">Embedding Model</label>
                    <span className="block text-sm text-muted-foreground">Jina</span>
                </div>
                <div className="mb-2">
                    <label className="block text-[0.85rem] font-semibold mb-0.5">Chunking Strategy</label>
                    <span className="block text-sm text-muted-foreground">By Page</span>
                </div>

                <div className="mb-2 mt-4 border-t pt-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <label className="block text-[0.85rem] font-semibold mb-0.5">PDF Page Images</label>
                            <p className="text-xs text-muted-foreground">
                                Extract each page of scanned/image-heavy PDFs as PNG images during processing.
                            </p>
                        </div>
                        <Switch
                            checked={pdfPageImages}
                            onCheckedChange={setPdfPageImages}
                        />
                    </div>
                </div>

            </div>
            <Dialog open={confirmationVisibility && !confirmation.current && !areValuesUnchanged()} onOpenChange={(state) => {

                if (state === false) {
                    setBtnDisable(false);
                }
                setConfirmationVisibility(state);
            }}>
                <DialogTrigger asChild>
                    <Button onClick={handleUpdateCollection} disabled={btnDiasable}>Update</Button>
                </DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Warning</DialogTitle>
                    </DialogHeader>
                    You have changed settings that would delete records in vector database which would require the collection to be reprocessed. Are you sure you want to proceed?
                    <div className="flex gap-2 mt-4 flex-row-reverse">
                        <Button variant="outline" onClick={() => {
                            setBtnDisable(false);
                            setConfirmationVisibility(false);
                        }} disabled={loading}>Cancel</Button>
                        <Button variant="default" onClick={() => {
                            setLoading(true);
                            requestToUpdate();
                        }} disabled={loading}>{loading ? 'Updating...' : 'Yes, Proceed'}</Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}